import type { ComponentType } from "react";
import { useState } from "react";
import { lazyWithRetry } from "@/utils/lazy-with-retry";

interface PreloadableModule {
  default: ComponentType<object>;
  /** For a route with nested lazy routes: loads the one `page` opens, or the default one. */
  preloadPage?: (page?: string) => Promise<unknown>;
}

// React.lazy suspends on its first render even when its module has already loaded, and React 19 then holds the
// Suspense reveal until 300 ms after the last fallback. Once preload() has resolved, this renders the component
// directly, so it never suspends.
export function lazyWithPreload(load: () => Promise<PreloadableModule>) {
  let loaded: ComponentType<object> | undefined;
  let loading: Promise<PreloadableModule> | undefined;

  const loadOnce = () =>
    (loading ??= load().then(
      (module) => {
        loaded = module.default;
        return module;
      },
      (error: unknown) => {
        // A later render or preload tries again.
        loading = undefined;
        throw error;
      },
    ));

  const Lazy = lazyWithRetry(loadOnce);

  function LazyWithPreload(props: object) {
    // Keep one component type for this element's lifetime, so a module that loads after it mounted doesn't remount it.
    const [Component] = useState<ComponentType<object>>(() => loaded ?? Lazy);
    return <Component {...props} />;
  }

  const preload = (page?: string) =>
    loadOnce().then((module) => module.preloadPage?.(page));

  return Object.assign(LazyWithPreload, { preload });
}
